/** Shapes crossing the API boundary, and the plaintext shapes behind it. */

export type ItemKind = 'video' | 'playlist' | 'settings';

/** Exactly what the server stores and returns. All of it is opaque but `kind`. */
export interface RawItem {
  uuid: string;
  kind: ItemKind;
  payload_b64: string;
  version: number;
  created_at: string;
  updated_at: string;
}

/** A decrypted item: server envelope plus the plaintext only we can read. */
export interface Item<T> {
  uuid: string;
  kind: ItemKind;
  version: number;
  updatedAt: string;
  payload: T;
}

export interface VariantRef {
  name: string;
  width: number;
  height: number;
  blob: string;
}

/**
 * One image of a slideshow, at every size it was encoded at.
 *
 * The same `VariantRef` a video rung uses, deliberately: the preload picker,
 * the cached-quality pills and the one-rung-per-thing cache reconciliation all
 * work on that shape, so slides get them without any of it learning a new type.
 */
export interface SlideRef {
  variants: VariantRef[];
}

/** kind=video — written by the CLI, read here (SPEC.md §5). */
export interface VideoPayload {
  title: string;
  duration: number;
  width: number;
  height: number;
  has_audio?: boolean;
  created: string;
  /** Absent on a slideshow, which has no HLS manifest and no content key. */
  master_blob?: string;
  content_key_wrapped?: string;
  thumb_blob: string | null;
  blobs: string[];
  variants?: VariantRef[];
  /**
   * Images, in order. Present only on a slideshow.
   *
   * A slideshow stays `kind: "video"` so it remains an ordinary library item —
   * one entry in a playlist, one row in a folder, the same preload and cache
   * accounting. Only the player looks at this field, and only to decide
   * whether to start hls.js or show pictures.
   */
  slides?: SlideRef[];
  /** How long each slide is shown. Absent means {@link DEFAULT_SLIDE_SECONDS}. */
  slide_seconds?: number;
  /**
   * The folder this video belongs to, or null/absent for unfiled.
   *
   * Membership lives here rather than as a list on the folder: one field, one
   * write to move a video, and a video cannot be in two folders or in the same
   * one twice because there is nowhere to say so.
   *
   * A folder id that no longer exists reads as unfiled, so deleting a folder
   * does not require touching every video it held.
   */
  folder?: string | null;
  /**
   * Position within its folder. Absent sorts last, newest first.
   *
   * One field serves every folder because a video is in exactly one — there is
   * no per-folder position to keep, and moving a video between folders carries
   * its place with it rather than needing a second write.
   */
  order?: number;
}

/** Videos in display order: explicit position first, then newest. */
export function sortVideos<T extends { payload: VideoPayload }>(videos: T[]): T[] {
  return [...videos].sort((a, b) => {
    const ao = a.payload.order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.payload.order ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    return a.payload.created < b.payload.created ? 1 : -1;
  });
}

/**
 * A folder. Just a name — the videos in it are the ones pointing at it.
 *
 * `items` is vestigial, from when playlists held their own membership list.
 * Kept only so an old record still parses; nothing reads it.
 */
export interface PlaylistPayload {
  name: string;
  created: string;
  /**
   * Position in the sidebar. Absent sorts last, then alphabetically.
   *
   * An explicit field rather than creation order: the order folders were made
   * in has nothing to do with the order they are wanted in.
   */
  order?: number;
  items?: string[];
}

/** Folders in display order: explicit position first, then name. */
export function sortFolders<T extends { payload: PlaylistPayload }>(folders: T[]): T[] {
  return [...folders].sort((a, b) => {
    const ao = a.payload.order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.payload.order ?? Number.MAX_SAFE_INTEGER;
    return ao - bo || a.payload.name.localeCompare(b.payload.name);
  });
}

/** One of 1, 1×2, 2×1, or 2×2 simultaneous players. */
export type LayoutId = 'single' | 'cols2' | 'rows2' | 'grid4';

export const LAYOUTS: { id: LayoutId; label: string; cells: number }[] = [
  { id: 'single', label: '1', cells: 1 },
  { id: 'cols2', label: '1×2', cells: 2 },
  { id: 'rows2', label: '2×1', cells: 2 },
  { id: 'grid4', label: '2×2', cells: 4 },
];

/**
 * Slots kept in state, regardless of the layout on screen.
 *
 * Cell assignments are stored for all of them and merely *displayed* down to
 * the current layout, so switching 2x2 -> 1 -> 2x2 brings the same videos back
 * rather than three empty panes.
 */
export const MAX_CELLS = 4;

/** Keeps a pane from being dragged away to nothing. */
export const MIN_SPLIT = 0.15;

export function clampSplit(value: number): number {
  return Math.min(1 - MIN_SPLIT, Math.max(MIN_SPLIT, value));
}

export function cellCount(layout: LayoutId): number {
  return LAYOUTS.find((l) => l.id === layout)?.cells ?? 1;
}

/** What each grid cell is playing. Persisted, encrypted, in the settings item. */
export interface CellState {
  /** Playlist uuid, or null when the cell is playing an ad-hoc queue. */
  playlist: string | null;
  /** Video uuids to play in order. */
  queue: string[];
  index: number;
}

export interface SettingsPayload {
  /**
   * Written on first unlock and checked on every later one. It is how we tell
   * "wrong passphrase" from "empty vault" before writing anything new.
   */
  canary: 'peepshow/v1';
  layout: LayoutId;
  cells: (CellState | null)[];
  /**
   * Output level per video uuid, 0..1. Absent is silent.
   *
   * Keyed by video rather than by slot: a quiet recording wants turning up
   * wherever it plays, and a loud one wants turning down. The slot it happens
   * to occupy is not a property of the audio.
   *
   * Kept in settings rather than on the video item so adjusting the volume
   * does not mean an optimistic-concurrency write against a record the CLI
   * also owns.
   */
  volumes: Record<string, number>;
  /**
   * Seconds per slide, per slideshow uuid. Absent uses what the CLI stored.
   *
   * Kept here rather than on the item for the same reason as volumes: changing
   * it should not mean an optimistic-concurrency write against a record the
   * CLI also owns — and how fast you want to look through a set is a property
   * of the looking, not of the set.
   */
  slideSpeeds?: Record<string, number>;
  /**
   * Hide the top bar, leaving the wall the whole window.
   *
   * Persisted with everything else: a wall set up to be looked at should come
   * back the way it was left, rather than having the chrome reappear on every
   * reload.
   */
  chromeHidden: boolean;
  /**
   * Where the grid divides, as fractions of the whole, 0..1.
   *
   * `x` is the vertical split between columns, `y` the horizontal one between
   * rows. Equal halves are rarely what you want: a portrait clip beside a
   * landscape one wastes most of its pane at 50/50.
   */
  split: { x: number; y: number };
}

/** Preload the highest rung a video has, whatever that happens to be. */
export const PRELOAD_BEST = -1;

/** Preload every rung. Not offered in the UI; still honoured if stored. */
export const PRELOAD_ALL = 0;

/**
 * Resolve a preload target to a single rung.
 *
 * `PRELOAD_BEST` is the highest rung present, resolved outright. `PRELOAD_ALL`
 * returns null so the caller fetches everything. A real height — what the
 * per-video picker passes — selects the best rung at or below it.
 *
 * Returns null when the payload predates per-rung metadata.
 */
export function pickVariant(
  variants: VariantRef[] | undefined,
  height: number,
): VariantRef | null {
  if (!variants?.length) return null;
  const ascending = [...variants].sort((a, b) => a.height - b.height);
  if (height === PRELOAD_BEST) return ascending[ascending.length - 1];
  if (height <= 0) return null;
  const atOrBelow = ascending.filter((v) => v.height <= height);
  return atOrBelow.length ? atOrBelow[atOrBelow.length - 1] : ascending[0];
}

/** How long a slide is shown when the payload does not say. */
export const DEFAULT_SLIDE_SECONDS = 5;

/** Is this a slideshow rather than a video? */
export function isSlideshow(payload: VideoPayload): boolean {
  return (payload.slides?.length ?? 0) > 0;
}

export function slideSeconds(payload: VideoPayload, override?: number): number {
  if (override && override > 0) return override;
  const seconds = payload.slide_seconds;
  return seconds && seconds > 0 ? seconds : DEFAULT_SLIDE_SECONDS;
}

/**
 * Where the slideshow goes after the slide at `index`.
 *
 * The end wraps to the start *and* reports itself finished: a cell holding
 * only this slideshow has nowhere to advance to, and stopping on the last
 * photo is not what a wall should do — while a cell with a queue wants to move
 * on. Doing both means neither case needs to know about the other.
 */
export function advanceSlide(index: number, count: number): { index: number; ended: boolean } {
  if (count <= 0) return { index: 0, ended: true };
  if (index + 1 < count) return { index: index + 1, ended: false };
  return { index: 0, ended: true };
}

/** Speeds the control cycles through, in seconds per slide. */
export const SLIDE_SPEEDS = [1, 2, 3, 5, 10];

/** The next speed in the ring, so one control covers all of them. */
export function nextSlideSpeed(current: number): number {
  const at = SLIDE_SPEEDS.indexOf(current);
  return SLIDE_SPEEDS[(at + 1) % SLIDE_SPEEDS.length] ?? SLIDE_SPEEDS[0];
}

/**
 * The smallest rung that still covers a box this wide, in device pixels.
 *
 * Deliberately not "the biggest available": a quarter-screen cell showing a
 * full-size photo spends the bytes and the decode time to produce pixels the
 * screen cannot show. Falls back to the largest rung when the box is bigger
 * than anything encoded, and to the smallest when the slide has nothing else.
 */
export function pickForWidth(variants: VariantRef[] | undefined, cssPixels: number): VariantRef | null {
  if (!variants?.length) return null;
  const ascending = [...variants].sort((a, b) => a.width - b.width);
  return ascending.find((v) => v.width >= cssPixels) ?? ascending[ascending.length - 1];
}

export function emptySettings(): SettingsPayload {
  return {
    canary: 'peepshow/v1',
    layout: 'single',
    cells: [null],
    volumes: {},
    chromeHidden: false,
    split: { x: 0.5, y: 0.5 },
  };
}
